import styled, { css } from 'styled-components';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonStyleProps {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth?: boolean;
}

const variantStyles = {
  primary: css`
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};

    &:hover {
      background: ${props => props.theme.colors.primaryHover};
      border-color: ${props => props.theme.colors.primaryHover};
    }
  `,
  secondary: css`
    background: ${props => props.theme.colors.secondary};
    border-color: ${props => props.theme.colors.secondary};
    color: ${props => props.theme.colors.white};

    &:hover {
      background: ${props => props.theme.colors.secondaryHover};
      border-color: ${props => props.theme.colors.secondaryHover};
    }
  `,
  outline: css`
    background: transparent;
    border-color: ${props => props.theme.colors.borderStrong};
    color: ${props => props.theme.colors.textPrimary};

    &:hover {
      border-color: ${props => props.theme.colors.primary};
      color: ${props => props.theme.colors.primary};
    }
  `,
  ghost: css`
    background: transparent;
    border-color: transparent;
    color: ${props => props.theme.colors.textPrimary};

    &:hover {
      background: ${props => props.theme.colors.grayLight};
    }
  `,
  danger: css`
    background: ${props => props.theme.colors.danger};
    border-color: ${props => props.theme.colors.danger};
    color: ${props => props.theme.colors.white};

    &:hover {
      background: ${props => props.theme.colors.red};
      border-color: ${props => props.theme.colors.red};
    }
  `
};

const sizeStyles = {
  sm: css`
    height: 32px;
    padding: 0 ${props => props.theme.spacing.md};
    font-size: ${props => props.theme.typography.sizes.sm};
  `,
  md: css`
    height: 40px;
    padding: 0 ${props => props.theme.spacing.lg};
    font-size: ${props => props.theme.typography.sizes.sm};
  `,
  lg: css`
    height: 48px;
    padding: 0 ${props => props.theme.spacing.xl};
    font-size: ${props => props.theme.typography.sizes.md};
  `
};

export const ButtonStyled = styled.button<ButtonStyleProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.xs};
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
  border-radius: ${props => props.theme.radii.md};
  border: 1px solid transparent;
  font-weight: ${props => props.theme.typography.weights.semibold};
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease, box-shadow 0.2s ease;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(46, 144, 250, 0.25);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  ${props => sizeStyles[props.$size]}
  ${props => variantStyles[props.$variant]}
`;
